import { forwardRef, useEffect, useState } from 'react';
import type { Highlight } from './Label';

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  placeholder?: string;
  /** @deprecated Was "render monospace"; the app is single-font now, so this only
   *  tightens the size. Don't add new call sites. */
  mono?: boolean;
  /** Width in px at the 16px baseline; converted to rem internally so the field
   *  grows with the root type scale. When omitted, the input flexes to fill the row. */
  width?: number;
  readOnly?: boolean;
  highlight?: Highlight;
  className?: string;
  ariaLabel?: string;
};

const baseInput =
  'min-w-0 bg-cmp-field ' +
  'border border-cmp-field-edge rounded ' +
  'px-1.5 py-[0.1875rem] text-[0.78125rem] text-cmp-field-fg h-6';

/**
 * Compact text input used densely throughout the OrderForm. Maintains
 * controlled state mirroring the prototype: external `value` updates flow
 * through, and `onChange` fires with the new string value.
 */
const TextInput = forwardRef<HTMLInputElement, Props>(function TextInput(
  {
    value,
    onChange,
    onBlur,
    placeholder,
    mono,
    width,
    readOnly,
    highlight,
    className,
    ariaLabel,
  },
  ref,
) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);

  // `mono` no longer switches typeface — the app is single-font (see
  // --app-font-family). It survives only as a slightly-tighter size for dense
  // code-like values; the prop is vestigial and should be dropped from callers.
  const monoCls = mono ? 'text-[0.75rem]' : '';
  // `!`-prefixed (Tailwind important) so the highlight wins over baseInput's
  // dark: variants — `dark:bg-slate-900` has specificity (0,2,0) which would
  // otherwise beat `bg-red-100` (0,1,0) under .dark.
  const hl =
    highlight === 'yellow'
      ? '!bg-yellow-50 dark:!bg-yellow-900/30 !border-yellow-500'
      : highlight === 'red'
        ? '!bg-red-100 dark:!bg-red-900/40 !border-red-600 dark:!border-red-800 !text-red-800 dark:!text-red-200 font-semibold'
        : '';
  const flexCls = width ? 'flex-none' : 'flex-1';

  return (
    <input
      ref={ref}
      type="text"
      value={v}
      placeholder={placeholder}
      readOnly={readOnly}
      aria-label={ariaLabel}
      onChange={(e) => {
        setV(e.target.value);
        onChange?.(e.target.value);
      }}
      onBlur={(e) => onBlur?.(e.target.value)}
      className={`${baseInput} ${monoCls} ${hl} ${flexCls} ${className ?? ''}`}
      style={width ? { width: `${width / 16}rem` } : undefined}
    />
  );
});

export default TextInput;
